import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { areValuesEqual } from '@utils/settings.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { fetchClinicById, updateClinic } from '@store/actions/clinic.action'
import { IClinic } from '@dataTypes/clinic.type'
import { toStateCode } from '@utils/address.util'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'

const LOADING_CONTAINER = 'tax-legal-settings'

export interface ITaxLegalForm {
  businessName: string
  businessNumber: string
  tinOrSsn: string
  idNumber: string
  vatNumber: string
  ein: string
  businessEntity: string
  taxYear: string
  npiType: string
  npiNumber: string
  stateLicenseNumber: string
  licenseExpiry: string
  address1: string
  address2: string
  city: string
  state: string
  zip: string
}

const EMPTY_FORM: ITaxLegalForm = {
  businessName: '',
  businessNumber: '',
  tinOrSsn: 'npi2',
  idNumber: '',
  vatNumber: '',
  ein: '',
  businessEntity: 'llc',
  taxYear: 'fiscal',
  npiType: 'npi2',
  npiNumber: '',
  stateLicenseNumber: '',
  licenseExpiry: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
}

const formValuesForCompare = (
  form: ITaxLegalForm,
): (string | boolean | number)[] => [
  form.businessName,
  form.businessNumber,
  form.tinOrSsn,
  form.idNumber,
  form.vatNumber,
  form.ein,
  form.businessEntity,
  form.taxYear,
  form.npiType,
  form.npiNumber,
  form.stateLicenseNumber,
  form.licenseExpiry,
  form.address1,
  form.address2,
  form.city,
  form.state,
  form.zip,
]

const clinicToForm = (clinic: IClinic): ITaxLegalForm => ({
  businessName: clinic.tax_business_name ?? '',
  businessNumber: clinic.tax_business_number ?? '',
  tinOrSsn: clinic.tax_tin_or_ssn_type ?? 'npi2',
  idNumber: clinic.tax_id_number ?? '',
  vatNumber: clinic.tax_vat_number ?? '',
  ein: clinic.tax_ein ?? '',
  businessEntity: clinic.tax_business_entity_type ?? 'llc',
  taxYear: clinic.tax_year_type ?? 'fiscal',
  npiType: clinic.tax_npi_type ?? 'npi2',
  npiNumber: clinic.tax_npi_number ?? '',
  stateLicenseNumber: clinic.state_license_number ?? '',
  licenseExpiry: clinic.state_license_expiry ?? '',
  address1: clinic.state_license_address ?? '',
  address2: clinic.state_license_address_line_2 ?? '',
  city: clinic.state_license_city ?? '',
  state:
    toStateCode(clinic.state_license_state) || clinic.state_license_state || '',
  zip: clinic.state_license_zip ?? '',
})

const formToClinicUpdates = (form: ITaxLegalForm): Partial<IClinic> => ({
  tax_business_name: form.businessName,
  tax_business_number: form.businessNumber,
  tax_tin_or_ssn_type: form.tinOrSsn,
  tax_id_number: form.idNumber,
  tax_vat_number: form.vatNumber,
  tax_ein: form.ein,
  tax_business_entity_type: form.businessEntity,
  tax_year_type: form.taxYear,
  tax_npi_type: form.npiType,
  tax_npi_number: form.npiNumber,
  state_license_number: form.stateLicenseNumber,
  state_license_expiry: form.licenseExpiry,
  state_license_address: form.address1,
  state_license_address_line_2: form.address2.trim() || null,
  state_license_city: form.city,
  state_license_state: form.state,
  state_license_zip: form.zip,
})

export interface ITaxLegalHooks {
  data: ITaxLegalForm
  setField: <K extends keyof ITaxLegalForm>(
    key: K,
    value: ITaxLegalForm[K],
  ) => void
  handleReset: () => void
  handleSave: () => Promise<void>
  hasChanges: boolean
  loading: boolean
  setHeaderButtons: Dispatch<SetStateAction<ReactNode>>
  clinicId: string
}

const useTaxLegal = (): ITaxLegalHooks => {
  const dispatch = useAppDispatch()
  const { setHeaderButtons } = useSettingsHeader()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''

  const [savedBaseline, setSavedBaseline] = useState<ITaxLegalForm>(EMPTY_FORM)
  const [data, setData] = useState<ITaxLegalForm>(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)

  useSettingsSubpageLoading(!dataReady)

  const hasChanges = useMemo(
    () =>
      !areValuesEqual(
        formValuesForCompare(data),
        formValuesForCompare(savedBaseline),
      ),
    [data, savedBaseline],
  )

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  const loadClinic = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      setSavedBaseline(EMPTY_FORM)
      setData(EMPTY_FORM)
      setDataReady(true)
      return
    }

    setLoading(true)
    try {
      const clinic = await dispatch(fetchClinicById(clinicId)).unwrap()
      const mapped = clinicToForm(clinic)
      setSavedBaseline(mapped)
      setData(mapped)
    } catch {
      showToast(
        createFailedToast(
          'Tax and legal',
          'Failed to load tax and legal settings',
        ),
      )
    } finally {
      setLoading(false)
      setDataReady(true)
    }
  }, [clinicId, dispatch, showToast])

  useEffect(() => {
    void loadClinic()
  }, [loadClinic])

  const setField = useCallback(
    <K extends keyof ITaxLegalForm>(key: K, value: ITaxLegalForm[K]): void => {
      setData((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const handleReset = useCallback((): void => {
    setData({ ...savedBaseline })
  }, [savedBaseline])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const updated = await dispatch(
        updateClinic({
          clinic_id: clinicId,
          updates: formToClinicUpdates(data),
        }),
      ).unwrap()
      const mapped = clinicToForm(updated)
      setSavedBaseline(mapped)
      setData(mapped)
      showToast(
        createSuccessToast(
          'Saved',
          'Tax and legal settings saved successfully.',
        ),
      )
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to save tax and legal settings'
      showToast(createFailedToast('Tax and legal', message))
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [clinicId, data, dispatch, hideLoading, showLoading, showToast])

  return {
    data,
    setField,
    handleReset,
    handleSave,
    hasChanges,
    loading,
    setHeaderButtons,
    clinicId,
  }
}

export default useTaxLegal
